import React from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import Button from 'design-system/button'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import { useTranslationRankmi } from 'helpers/translations.helper'
import CheckBoxRadioButtonInput from 'components/ui/forms/field/checkbox-radiobutton/CheckBoxRadioButtonInput'
import { useField } from '../../../../../context'
import { prefix } from '../../common'
import { Text } from './Text'
import styles from './ConfigModal.module.scss'

interface IConfig {
  show: boolean
  className?: string
  title?: any
  defaultValue?: string
  acceptButtonText?: string
  onClose(): void
  showCancel?: boolean
}

const Config: React.FC<IConfig> = ({
  show,
  className = '',
  title = '',
  defaultValue,
  onClose,
  showCancel = true,
}) => {
  const reminderNotification = useField('reminderNotification')

  const [isDefault, setIsDefault] = React.useState(!!defaultValue)
  const { t } = useTranslation()

  const tRelative = useTranslationRankmi({
    prefix: `${prefix}.config`,
  })

  const [textValue, setTextValue] = React.useState(defaultValue || '')

  const handleOnAccept = () => {
    reminderNotification.change({
      active: true,
      ...reminderNotification.value,
      emailBody: textValue,
    })
    onClose()
  }
  const options = [
    {
      checked: !isDefault,
      label: tRelative('values.default'),
      classNameChecked: styles.synchronousChecked,
      onSelect: () => {
        setTextValue('')
        setIsDefault(false)
      },
    },
    {
      checked: isDefault,
      label: tRelative('values.custom'),
      classNameChecked: styles.synchronousChecked,
      children: (
        <>
          <div className={styles.divider} />
          <div className={styles.synchronousInput}>
            <Text value={textValue} onChange={(value) => setTextValue(value)} />
          </div>
        </>
      ),
      onSelect: () => {
        setIsDefault(true)
        setTextValue(defaultValue || '')
      },
    },
  ]

  return (
    <Modal
      show={show}
      size="lg"
      centered
      className={`${styles.modalContainer} ${className}`}
      onHide={onClose}
    >
      <Modal.Header
        translate="yes"
        closeButton
        className={`${styles.modalHeader} d-flex align-items-center`}
      >
        <h1 className="title m-0">{title || tRelative('label')}</h1>
      </Modal.Header>
      <Modal.Body className="px-4 py-4">
        {options.map(({ label, onSelect, checked, children }, index) => (
          <React.Fragment key={index}>
            <CheckBoxRadioButtonInput
              key={index}
              name={'config-notifications-' + index}
              checked={checked}
              inline={false}
              type={'radio'}
              label={label}
              onChange={() => {
                onSelect?.()
              }}
            />
            {checked && children}
          </React.Fragment>
        ))}
      </Modal.Body>
      <div className="px-4 d-flex justify-content-center">
        <div className="dropdown-divider m-0 w-100 " />
      </div>
      <Modal.Footer className="border-0">
        {showCancel && (
          <Button
            size={ButtonSizes.Small}
            variant={ButtonVariant.Secondary}
            className="my-0"
            onClick={onClose}
          >
            {t('components.modal.buttons.cancel')}
          </Button>
        )}
        <Button
          size={ButtonSizes.Small}
          variant={ButtonVariant.Primary}
          className="my-0"
          onClick={handleOnAccept}
        >
          {t('components.modal.buttons.accept')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default Config
